/**
 * v2 Landing — typeahead search for pre-launch concepts.
 *
 * Uses the portal-based `V2AsyncSearchInput` (modeled after the BD Portal
 * Wiki landing search). The input is locally-stateful for typing
 * responsiveness; suggestion fetches go through the v2 store's
 * `fetchSuggestions` (which cancels in-flight requests on each new
 * keystroke). On select, the store opens the right-side sidebar.
 */

import { useCallback, useState } from 'react';
import { observer } from 'mobx-react';

import { useStore } from 'hooks/useStore';
import { PreLaunchConceptSuggestion } from 'Interfaces/FDPInterfaces/provisionalForecastV2Interfaces';
import V2AsyncSearchInput from './V2AsyncSearchInput';

const ConceptSuggestSearch = observer(() => {
  const {
    ProvisionalForecastV2Store: { fetchSuggestions, selectConceptAndOpenSidebar },
    UserStore: { userMetadata },
  } = useStore();
  const userEmail = userMetadata?.userInfo?.email ?? null;

  // Local input state — keeping this out of MobX lets the keystrokes
  // feel instant without waiting for store updates / re-renders, and
  // matches the wiki search's pattern.
  const [inputValue, setInputValue] = useState('');

  // Stable callback so V2AsyncSearchInput's useEffect dependency stays
  // referentially equal between renders.
  const loadOptions = useCallback(
    (query: string) => fetchSuggestions(query),
    [fetchSuggestions]
  );

  // Click-on-suggestion does NOT replace the input or close the
  // dropdown — it just opens the sidebar for that concept. The user
  // can keep their typed query visible, browse other suggestions, or
  // pick a different one. The picked row stays highlighted in the
  // dropdown via `closeOnSelect={false}` in the underlying input.
  const handleSelect = (option: PreLaunchConceptSuggestion) => {
    selectConceptAndOpenSidebar(option, userEmail);
  };

  const handleClear = () => {
    setInputValue('');
  };

  return (
    <V2AsyncSearchInput<PreLaunchConceptSuggestion>
      inputValue={inputValue}
      onInputChange={setInputValue}
      onSelect={handleSelect}
      onClear={handleClear}
      loadOptions={loadOptions}
      placeholder="Search by Concept name or GFC…"
      minLength={2}
      debounceMs={500}
      closeOnSelect={false}
      getOptionKey={o => `${o.GFC}-${o.Concept}`}
      itemTemplate={(c) => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            width: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
            }}
          >
            <span
              style={{
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: '0.95rem',
              }}
            >
              {c.Concept}
            </span>
            <span style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
              GFC {c.GFC}
            </span>
          </div>
        </div>
      )}
    />
  );
});

export default ConceptSuggestSearch;
